import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Paper } from '@mui/material';
import { AppDispatch } from '../../shared/state/store';
import { RootState } from '../../shared/state/root-reducer';
import { getPtRecordV2 } from '../../patient/state/patient-thunk';
import { AxiosGetPayload } from '../../shared/service/axios-get-payload';
import { showErrorStatus } from '../../security/state/user-slice';
import { ClientCrm, ClientStatusCardViewModel } from '../../shared/model/client-status-card';
import { crmInboundErrorMessages } from '../../shared/enums';
import { PatientRecordResV2 } from '../../patient/state/patient-slice';
import MobilePaymentsLoader from '../../shared/components/mobile-payments-loader';
import classes from './CrmIntegration.module.css';

interface CrmIntegrationProps {
  // Add props as needed
}

const CrmIntegration: React.FC<CrmIntegrationProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [queryParams, setQueryParams] = useState<any>();
  const [currentClientId, setCurrentClientId] = useState<any>();
  const isInit = useRef(false);

  const allClients: ClientStatusCardViewModel[] = useSelector((state: RootState) => {
    return state.implementationContext.implementationSpecialistClient.allClients;
  });

  const isLoadingClients = useSelector((state: RootState) => {
    return state.implementationContext.implementationSpecialistClient.isLoading;
  });

  const patientRecordInfo = useSelector((state: RootState) => {
    return state?.patientContext.inboundPtRecordV2;
  });

  const handleGenericError = async (errorMessage: string) => {
    await dispatch(showErrorStatus(errorMessage));
    navigate(`/patients`);
  };

  const getPtRecordData = async (
    crmId: string,
    clientId: number,
    op?: string
  ) => {
    const clientItem: ClientStatusCardViewModel | undefined = allClients.find(
      (client: ClientStatusCardViewModel) => client.clientId === clientId
    );
    
    // Check if client exists
    if (!clientItem) {
      console.log('CRM route: Client not found');
      return handleGenericError(crmInboundErrorMessages.misconfigured);
    }
    
    if (!clientItem?.isActive) {
      console.log('CRM route: Client is inactive');
      return handleGenericError(crmInboundErrorMessages.inactiveClient);
    }
    
    const clientCrm: ClientCrm[] = clientItem?.clientCrm || [];
    
    // Show error if CRM is not configured
    if (!clientCrm || clientCrm?.length === 0) {
      console.log('CRM route: CRM is not configured');
      return handleGenericError(crmInboundErrorMessages.misconfigured);
    }

    const ptRecordRes: AxiosGetPayload = await dispatch(
      getPtRecordV2({ crmId: crmId, clientId: clientId, crm: clientCrm[0], op: op })
    );
    
    let errorMessage: string = crmInboundErrorMessages.misconfigured;
    if (ptRecordRes.error) {
      if (
        ptRecordRes.meta.arg.crmId === undefined ||
        ptRecordRes.payload === undefined
      ) {
        errorMessage = crmInboundErrorMessages.patientError;
      } else if (Number.isNaN(ptRecordRes.meta.arg.clientId)) {
        errorMessage = crmInboundErrorMessages.facilityError;
      }
      dispatch(showErrorStatus(errorMessage));
      navigate(`/patients`);
    } else if (typeof ptRecordRes.payload === 'string') {
      if (ptRecordRes.payload === 'No facility to process.') {
        errorMessage = crmInboundErrorMessages.facilityError;
      }
      dispatch(showErrorStatus(errorMessage));
      navigate(`/patients`);
    }
  };

  useEffect(() => {
    const query = window.location.search;
    const urlParams = new URLSearchParams(query);
    const formedParams = Object.fromEntries(urlParams);

    setQueryParams(query);
    setCurrentClientId(formedParams.clientId);

    // Wait for clients to finish loading before processing
    if (isLoadingClients) {
      console.log('CRM route: Clients are still loading');
      return;
    }

    if (!isInit.current && allClients.length > 0) {
      isInit.current = true;
      if (formedParams.crmId && formedParams.clientId) {
        console.log('CRM route: Getting pt record data');
        getPtRecordData(
          formedParams.crmId,
          Number(formedParams.clientId),
          formedParams.op
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allClients, isLoadingClients]);

  useEffect(() => {
    if (patientRecordInfo && patientRecordInfo.url) {
      navigate(patientRecordInfo.url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientRecordInfo]);

  return (
    <>
      <Paper className={classes.splashStyle}>
        <Grid
          container
          spacing={1}
          direction="row"
          style={{ paddingTop: "1em", paddingLeft: "3em" }}
        >
          <MobilePaymentsLoader showLoader={true} customMessage={crmInboundErrorMessages.loadingMsg} />
        </Grid>
      </Paper>
    </>
  );
};

export default CrmIntegration;

